import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  createClientCreativeKnowledge,
  deleteClientCreativeKnowledge,
  fetchClientCreativeKnowledge,
  fetchClientOffers,
  updateClientCreativeKnowledge,
} from '@src/api';
import { fetchVerticals } from '@src/api/actions/verticals';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const EMPTY_VERTICAL_ASSET = {
  id: undefined,
  vertical: '',
  general_asset_folder_url: '',
  recent_ad_examples_url: '',
};

const EMPTY_RECORD = {
  client: '',
  offers: [],
  website_url: '',
  client_asset_folder_url: '',
  logo_url: '',
  notes: '',
  vertical_assets: [],
};

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    return String(data.non_field_errors[0]);
  }
  if (data && typeof data === 'object') {
    const firstValue = Object.values(data).find((value) => (Array.isArray(value) ? value.length > 0 : Boolean(value)));
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (typeof firstValue === 'string' && firstValue.trim()) return firstValue;
  }
  return fallback;
}

function ExternalLink({ href, label }) {
  if (!href) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      startIcon={<LinkIcon fontSize="small" />}
      sx={{ px: 0.5, minWidth: 0, justifyContent: 'flex-start' }}
    >
      {label}
    </Button>
  );
}

export default function ClientCreativeKnowledgePage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialClientId = String(location?.state?.clientId ?? searchParams.get('client_id') ?? '');

  const [rows, setRows] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [offerOptions, setOfferOptions] = useState([]);
  const [filterClient, setFilterClient] = useState(initialClientId);
  const [filterVertical, setFilterVertical] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [current, setCurrent] = useState(EMPTY_RECORD);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
    addOption: addClientOption,
  } = useClientAutocomplete();

  const loadVerticals = async () => {
    try {
      const res = await fetchVerticals();
      setVerticals(Array.isArray(res.data) ? res.data : []);
    } catch (_err) {
      // Keep page usable if verticals fail.
    }
  };

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchClientCreativeKnowledge({
        client_id: filterClient || undefined,
        vertical_id: filterVertical || undefined,
        search: search || undefined,
      });
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
      data.forEach((row) => {
        if (row.client && row.client_name) {
          addClientOption({ value: row.client, label: row.client_name });
        }
      });
      setRows(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load creative knowledge'));
    } finally {
      setLoading(false);
    }
  };

  const loadOffers = async (clientId) => {
    if (!clientId) {
      setOfferOptions([]);
      return;
    }
    try {
      const res = await fetchClientOffers({ client_id: clientId });
      const data = Array.isArray(res.data) ? res.data : [];
      setOfferOptions(data);
    } catch (_err) {
      setOfferOptions([]);
    }
  };

  useEffect(() => {
    loadVerticals();
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentClientId = searchParams.get('client_id') || '';
    const nextClientId = filterClient ? String(filterClient) : '';
    if (currentClientId === nextClientId) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    if (nextClientId) {
      nextParams.set('client_id', nextClientId);
    } else {
      nextParams.delete('client_id');
    }
    setSearchParams(nextParams, { replace: true });
  }, [filterClient, searchParams, setSearchParams]);

  useEffect(() => {
    loadOffers(current.client);
  }, [current.client]);

  const handleOpen = (row = EMPTY_RECORD) => {
    const nextClient = row.client || '';
    setCurrent({
      ...EMPTY_RECORD,
      ...row,
      client: nextClient,
      offers: Array.isArray(row.offers) ? row.offers : [],
      vertical_assets: Array.isArray(row.vertical_assets)
        ? row.vertical_assets.map((item) => ({
          id: item.id,
          vertical: item.vertical || '',
          general_asset_folder_url: item.general_asset_folder_url || '',
          recent_ad_examples_url: item.recent_ad_examples_url || '',
        }))
        : [],
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setCurrent(EMPTY_RECORD);
    setOfferOptions([]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = {
      client: current.client,
      offers: current.offers,
      website_url: current.website_url,
      client_asset_folder_url: current.client_asset_folder_url,
      logo_url: current.logo_url,
      notes: current.notes,
      vertical_assets: current.vertical_assets
        .filter((item) => item.vertical)
        .map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          vertical: item.vertical,
          general_asset_folder_url: item.general_asset_folder_url || '',
          recent_ad_examples_url: item.recent_ad_examples_url || '',
        })),
    };
    try {
      if (current.id) {
        await updateClientCreativeKnowledge(current.id, payload);
      } else {
        await createClientCreativeKnowledge(payload);
      }
      handleClose();
      await loadRows();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to save creative knowledge'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this creative knowledge record?')) return;
    setError('');
    try {
      await deleteClientCreativeKnowledge(id);
      await loadRows();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to delete creative knowledge'));
    }
  };

  const visibleRows = useMemo(() => rows, [rows]);

  const selectedOffers = offerOptions.filter((offer) => current.offers.includes(offer.id));

  const addVerticalAssetRow = () => {
    setCurrent((prev) => ({
      ...prev,
      vertical_assets: [...prev.vertical_assets, { ...EMPTY_VERTICAL_ASSET }],
    }));
  };

  const updateVerticalAssetRow = (index, field, value) => {
    setCurrent((prev) => ({
      ...prev,
      vertical_assets: prev.vertical_assets.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      )),
    }));
  };

  const removeVerticalAssetRow = (index) => {
    setCurrent((prev) => ({
      ...prev,
      vertical_assets: prev.vertical_assets.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const getOfferLabel = (offer) => offer.display_label || offer.name || `Offer ${offer.id}`;

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" fontWeight={700}>
          Client Creative Knowledge
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add Record
          </Button>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={clientOptions}
                loading={clientOptionsLoading}
                value={findClientOptionByValue(filterClient) || null}
                onInputChange={(_event, value) => handleClientSearch(value)}
                onChange={(_event, value) => setFilterClient(value?.value || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Client" placeholder="All clients" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Vertical"
                value={filterVertical}
                onChange={(event) => setFilterVertical(event.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {verticals.map((vertical) => (
                  <MenuItem key={vertical.id} value={vertical.id}>
                    {vertical.vertical}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Client, notes, link, offer"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={loadRows} disabled={loading} sx={{ height: '100%' }}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Website</TableCell>
                    <TableCell>Client Assets</TableCell>
                    <TableCell>Logo</TableCell>
                    <TableCell>Offers</TableCell>
                    <TableCell>Vertical Assets</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No creative knowledge records found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{row.client_name || row.client}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.parent_company_name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell><ExternalLink href={row.website_url} label="Website" /></TableCell>
                        <TableCell><ExternalLink href={row.client_asset_folder_url} label="Assets" /></TableCell>
                        <TableCell><ExternalLink href={row.logo_url} label="Logo" /></TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            {(row.offer_labels || []).length
                              ? row.offer_labels.map((label) => (
                                <Chip key={`${row.id}-${label}`} size="small" label={label} />
                              ))
                              : <Typography variant="body2" color="text.secondary">—</Typography>}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 280 }}>
                          <Stack spacing={1}>
                            {(row.vertical_assets || []).length ? row.vertical_assets.map((asset) => (
                              <Box key={asset.id}>
                                <Typography variant="body2" fontWeight={600}>
                                  {asset.vertical_name || asset.vertical}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <ExternalLink href={asset.general_asset_folder_url} label="General Assets" />
                                  <ExternalLink href={asset.recent_ad_examples_url} label="Recent Ads" />
                                </Stack>
                              </Box>
                            )) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography variant="body2" sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                          >
                            {row.notes || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpen(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{current.id ? 'Edit Creative Knowledge' : 'Add Creative Knowledge'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Autocomplete
              options={clientOptions}
              loading={clientOptionsLoading}
              value={findClientOptionByValue(current.client) || null}
              onInputChange={(_event, value) => handleClientSearch(value)}
              onChange={(_event, value) => setCurrent((prev) => ({
                ...prev,
                client: value?.value || '',
                offers: [],
              }))}
              renderInput={(params) => (
                <TextField {...params} label="Client" required />
              )}
            />

            <Autocomplete
              multiple
              options={offerOptions}
              getOptionLabel={getOfferLabel}
              value={selectedOffers}
              onChange={(_event, value) => setCurrent((prev) => ({
                ...prev,
                offers: value.map((item) => item.id),
              }))}
              renderInput={(params) => (
                <TextField {...params} label="Offers" placeholder="Select client offers" />
              )}
              disabled={!current.client}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Website URL"
                  type="url"
                  fullWidth
                  value={current.website_url}
                  onChange={(event) => setCurrent((prev) => ({ ...prev, website_url: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Client Asset Folder URL"
                  type="url"
                  fullWidth
                  value={current.client_asset_folder_url}
                  onChange={(event) => setCurrent((prev) => ({ ...prev, client_asset_folder_url: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Logo URL"
                  type="url"
                  fullWidth
                  value={current.logo_url}
                  onChange={(event) => setCurrent((prev) => ({ ...prev, logo_url: event.target.value }))}
                />
              </Grid>
            </Grid>

            <TextField
              label="Notes"
              multiline
              minRows={4}
              value={current.notes}
              onChange={(event) => setCurrent((prev) => ({ ...prev, notes: event.target.value }))}
              placeholder="Internal creative notes, positioning, visual constraints, brand direction, territory nuances..."
            />

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>
                Vertical Assets
              </Typography>
              <Button startIcon={<AddIcon />} onClick={addVerticalAssetRow}>
                Add Vertical Row
              </Button>
            </Stack>

            {current.vertical_assets.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No vertical-specific shared assets added yet.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {current.vertical_assets.map((row, index) => (
                  <Card key={row.id || `new-${index}`} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2">Vertical Row {index + 1}</Typography>
                        <IconButton size="small" color="error" onClick={() => removeVerticalAssetRow(index)}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            select
                            fullWidth
                            label="Vertical"
                            value={row.vertical}
                            onChange={(event) => updateVerticalAssetRow(index, 'vertical', event.target.value)}
                          >
                            {verticals.map((vertical) => (
                              <MenuItem key={vertical.id} value={vertical.id}>
                                {vertical.vertical}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={4.5}>
                          <TextField
                            fullWidth
                            label="General Asset Folder URL"
                            type="url"
                            value={row.general_asset_folder_url}
                            onChange={(event) => updateVerticalAssetRow(index, 'general_asset_folder_url', event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4.5}>
                          <TextField
                            fullWidth
                            label="Recent Ad Examples URL"
                            type="url"
                            value={row.recent_ad_examples_url}
                            onChange={(event) => updateVerticalAssetRow(index, 'recent_ad_examples_url', event.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !current.client}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
